var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as THREE from "../../src/insole-web-common/vendor/three/build/three.module.js";
import { DeepClone, Mutex } from "../insole-web-common/src/util.js";
import { ConvertHPMetadata } from "../metadata/metadata-hp.js";
import { ConvertPrescriptionToMetadata } from "./adapter-client/adapter-client.js";
import { CreateEngine, ImportFootScanMesh } from "./common.js";
import { InsoleInspectionScene } from "./insole-inspection-scene.js";
import { LandmarkScene } from "./landmark-scene.js";
import { IsNotNullish, IsNullish } from "../util.js";
import { GetCurrentMetadataVersion, MetadataBackwardCompatibilityConversion, } from "../metadata/metadata-backwards-compatibility.js";
import { isLocalhost } from "../configs.js";
export var EngineActivationResult;
(function (EngineActivationResult) {
    EngineActivationResult[EngineActivationResult["Success"] = 0] = "Success";
    EngineActivationResult[EngineActivationResult["NetworkError"] = 1] = "NetworkError";
    EngineActivationResult[EngineActivationResult["ActivationError"] = 2] = "ActivationError";
})(EngineActivationResult || (EngineActivationResult = {}));
// All metadata parameters that could be set from a prescription
const prescriptionParameterKeys = [
    "shell.thickness",
    "firstRayCutout",
    "heelSeat",
    "hpSpecific.archModification",
    "hpSpecific.widthModification",
    "medialFlange",
    "lateralFlange",
    "heelPost",
    "forefootPost",
    "hpSpecific.advancedForefootPost",
    "hpSpecific.archWedge",
    "kirbySkive",
    "mortonsExtension",
    "cutout",
    "domesAndWells",
    "regionBasedWidth",
    "sulcus",
];
export class Visualizer {
    /**
     * Creates a visualizer instance
     * After creation, the `rootNode` should be added to the document
     * @param enablerServiceUrl
     * @param enablerServiceToken Access token for the enabler service
     */
    constructor({ enablerServiceUrl, enablerServiceBearerTokenGetter }) {
        this.activeScene = null;
        this.engines = [null, null];
        this.engineLocks = [new Mutex(), new Mutex()];
        this.rootNode = document.createElement("div");
        this.rootNode.style.position = "relative";
        this.rootNode.style.width = "100%";
        this.rootNode.style.height = "100%";
        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.setClearColor(0xffffff);
        this.canvas = this.renderer.domElement;
        this.canvas.addEventListener("contextmenu", ev => ev.preventDefault());
        this.rootNode.appendChild(this.canvas);
        this.enablerServiceUrl = enablerServiceUrl;
        this.enablerServiceBearerTokenGetter = enablerServiceBearerTokenGetter;
        const resizeObserver = new ResizeObserver(() => this.updateSize());
        resizeObserver.observe(this.rootNode);
    }
    /**
     * Activates landmark selection scene
     * @param initData Data to initialize the scene
     * @returns True if the scene could be loaded, false otherwise (which indicates that `initData` contains invalid data)
     */
    setLandmarkScene(initData) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.activateScene(yield LandmarkScene.create(this, this.renderer, initData));
        });
    }
    /**
     * Activates insole inspection scene
     * @param initData Data to initialize the scene
     * @returns True if the scene could be loaded, false otherwise (which indicates that `initData` contains invalid data)
     */
    setInsoleInspectionScene(initData) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.activateScene(yield InsoleInspectionScene.create(this, this.renderer, initData, this.rootNode));
        });
    }
    /**
     * Gets the landmarks of the foot scans (when the landmark selection scene is active)
     * @returns Landmarks of the foot scans. Returns null if not in landmark selection scene.
     * Individual landmarks can be null if the foot scan for that side is not loaded,
     * or if no landmarks are set (e.g. because they could not be detected automatically)
     */
    getFootScanLandmarks() {
        if (!(this.activeScene instanceof LandmarkScene)) {
            return null;
        }
        return this.activeScene.getLandmarks();
    }
    /**
     * Resets the landmarks of both foot scans to their initial values.
     * Does nothing if not in landmark selection scene.
     */
    resetLandmarks() {
        if (!(this.activeScene instanceof LandmarkScene)) {
            return;
        }
        return this.activeScene.resetLandmarks();
    }
    /**
     * Gets the metadata (when the insole inspection scene is active)
     * @returns The metadata in leo metadata format, or null if there is no metadata for the given foot side.
     */
    getMetadata() {
        var _a, _b;
        return (_b = (_a = this.activeScene) === null || _a === void 0 ? void 0 : _a.getMetadata()) !== null && _b !== void 0 ? _b : null;
    }
    /**
     * Updates the insole(s) from the given metadata.
     * Only works in the insole inspection scene.
     */
    applyMetadata(metadata) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!(this.activeScene instanceof InsoleInspectionScene)) {
                return;
            }
            MetadataBackwardCompatibilityConversion(metadata.left, null);
            MetadataBackwardCompatibilityConversion(metadata.right, null);
            yield this.activeScene.applyMetadata(metadata);
        });
    }
    acquireEngine(side) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.engineLocks[side].lock();
            if (this.engines[side] === null) {
                this.engines[side] = CreateEngine(side === 0 /* FootSide.Left */ ? "left" : "right");
                yield this.engines[side].ready;
                if (side == 0 /* FootSide.Left */) {
                    const engineInfo = yield this.engines[side].GetEngineInfo();
                    window.LeoIceEngineInfo = engineInfo;
                }
            }
            return this.engines[side];
        });
    }
    releaseEngine(side) {
        this.engineLocks[side].unlock();
    }
    /**
     * Updates the parameters of the metadata from the prescription, returning a new metadata object.
     * The previous metadata is not modified.
     */
    updateMetadataFromPrescription(prescription, previousMetadata, leftSide, rightSide, leftScanData, rightScanData) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const converted = ConvertPrescriptionToMetadata(prescription, null, null);
            const prescriptionMetadata = ConvertHPMetadata({
                leftFootMetadata: leftSide ? converted.leftFootMetadata : null,
                rightFootMetadata: rightSide ? converted.rightFootMetadata : null,
            });
            const HandleFootscanMetadataBackwardCompatibility = (side, metadata, scanData) => __awaiter(this, void 0, void 0, function* () {
                if (IsNotNullish(metadata)) {
                    if (metadata.version !== GetCurrentMetadataVersion() && IsNotNullish(scanData)) {
                        const engine = yield this.acquireEngine(side);
                        yield engine.SetDefaultInsoleParameters();
                        const importResult = yield ImportFootScanMesh(engine, scanData);
                        this.releaseEngine(side);
                        if (importResult !== null && importResult.originalBBoxCenter !== null) {
                            MetadataBackwardCompatibilityConversion(metadata, importResult.originalBBoxCenter.y);
                        }
                    }
                }
            });
            yield Promise.all([
                HandleFootscanMetadataBackwardCompatibility(0 /* FootSide.Left */, prescriptionMetadata.left, leftScanData),
                HandleFootscanMetadataBackwardCompatibility(1 /* FootSide.Right */, prescriptionMetadata.right, rightScanData),
            ]);
            const previousMetadataCloned = DeepClone(previousMetadata);
            if (!leftSide) {
                previousMetadataCloned.left = null;
                prescriptionMetadata.left = null;
            }
            if (!rightSide) {
                previousMetadataCloned.right = null;
                prescriptionMetadata.right = null;
            }
            function IsObject(val) {
                return val !== undefined && val !== null && typeof val === "object" && !Array.isArray(val);
            }
            function MergeObjectsRecursive(target, source) {
                if (!IsObject(target) || !IsObject(source)) {
                    return;
                }
                for (const key in source) {
                    const srcKey = key;
                    if (IsObject(source[srcKey])) {
                        if (!(key in target)) {
                            Object.assign(target, { [key]: {} });
                        }
                        MergeObjectsRecursive(target[key], source[srcKey]);
                    }
                    else {
                        Object.assign(target, { [key]: source[srcKey] });
                    }
                }
            }
            MergeObjectsRecursive(previousMetadataCloned, prescriptionMetadata);
            // Overwrite prescription related fields
            for (const side of ["left", "right"]) {
                let prevSideMetadata = previousMetadataCloned[side];
                const prescriptionSideMetadata = prescriptionMetadata[side];
                if (IsNullish(prescriptionSideMetadata)) {
                    continue;
                }
                if (IsNullish(prevSideMetadata)) {
                    prevSideMetadata = previousMetadataCloned[side] = { version: GetCurrentMetadataVersion() };
                }
                for (const key of prescriptionParameterKeys) {
                    const segments = key.split(".");
                    const propertyName = segments[segments.length - 1];
                    let srcObject = prescriptionSideMetadata;
                    let targetObject = prevSideMetadata;
                    for (let i = 0; i < segments.length - 1; ++i) {
                        if (IsNullish(srcObject)) {
                            break;
                        }
                        const segment = segments[i];
                        srcObject = srcObject[segment];
                    }
                    const srcValue = IsNullish(srcObject) ? null : srcObject[propertyName];
                    if (IsNullish(srcValue)) {
                        // Remove this property from targetObject
                        for (let i = 0; i < segments.length - 1; ++i) {
                            if (IsNullish(targetObject)) {
                                break;
                            }
                            const segment = segments[i];
                            targetObject = targetObject[segment];
                        }
                        if (IsNotNullish(targetObject)) {
                            delete targetObject[propertyName];
                        }
                        continue;
                    }
                    // Overwrite property in targetObject
                    for (let i = 0; i < segments.length - 1; ++i) {
                        const segment = segments[i];
                        targetObject = (_a = targetObject[segment]) !== null && _a !== void 0 ? _a : (targetObject[segment] = {});
                    }
                    targetObject[propertyName] = srcValue;
                }
            }
            return previousMetadataCloned;
        });
    }
    ActivateEngine(engine) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isLocalhost) {
                return 0 /* EngineActivationResult.Success */;
            }
            yield engine.ready;
            const randomValue = yield engine.EnablerServiceGetRandomValue();
            let jsonString = "";
            try {
                const req = yield fetch(this.enablerServiceUrl, {
                    method: "POST",
                    headers: Object.assign({ "Content-Type": "application/json" }, (this.enablerServiceBearerTokenGetter && {
                        Authorization: `Bearer ${this.enablerServiceBearerTokenGetter()}`,
                    })),
                    body: JSON.stringify({
                        Domain: window.location.hostname,
                        version: 0,
                        timestamp: new Date().toISOString(),
                        randomValue: randomValue,
                    }),
                });
                if (req.redirected || !req.ok) {
                    return 1 /* EngineActivationResult.NetworkError */;
                }
                jsonString = yield req.text();
            }
            catch (ex) {
                return 1 /* EngineActivationResult.NetworkError */;
            }
            const success = yield engine.EnablerServiceVerifyMessage(jsonString);
            return success ? 0 /* EngineActivationResult.Success */ : 2 /* EngineActivationResult.ActivationError */;
        });
    }
    updateSize() {
        var _a;
        (_a = this.activeScene) === null || _a === void 0 ? void 0 : _a.setSize(this.rootNode.clientWidth, this.rootNode.clientHeight);
    }
    activateScene(scene) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            (_a = this.activeScene) === null || _a === void 0 ? void 0 : _a.dispose();
            this.activeScene = scene;
            if (scene === null) {
                return false;
            }
            this.updateSize();
            return true;
        });
    }
    /**
     * Disposes the visualizer, freeing up any native resources (webassembly modules, 3D models, textures, etc.)
     * Should be called when the visualizer is no longer needed, but the page is still used.
     * There is no need to call this on page close/refresh, because the browser does it automatically.
     */
    dispose() {
        var _a, _b, _c;
        (_a = this.activeScene) === null || _a === void 0 ? void 0 : _a.dispose();
        this.activeScene = null;
        (_b = this.engines[0 /* FootSide.Left */]) === null || _b === void 0 ? void 0 : _b.Destroy();
        (_c = this.engines[1 /* FootSide.Right */]) === null || _c === void 0 ? void 0 : _c.Destroy();
    }
}
//# sourceMappingURL=visualizer.js.map